import { makeAutoObservable, runInAction } from "mobx";
import { PaginatedItems } from "../../models/common";
import { ITableUsageRecord, IUsageData, IUsageSummary } from "../../models/usage";
import QueryService from "../../utils/QueryService";

export interface IAccountStore {
	isLoading: boolean;
	isLoadingSummaryReport: boolean;
	isLoadingAllUsageData: boolean;
	isError: boolean;
	rowsPerPage: number;
	usageSummary: IUsageSummary;
	usageData: IUsageData[];
	allUsageData: IUsageData[];
	tabularUsageData: PaginatedItems<ITableUsageRecord>;

	clearStoreData(): void;

	loadAllUsageData(
		startDate: string,
		endDate: string,
		selectedApiRoute: string[],
		usageType: string[],
		selectedTailoredModels: number[],
		getHistoricalData?: boolean,
		calcUnits?: boolean,
	): Promise<IUsageData[]>;

	fetchVideoSecondsData(
		startDate: string,
		endDate: string,
		selectedApiRoute: string[],
		usageType: string[],
	): Promise<IUsageData[]>;
}

export default class AccountStore implements IAccountStore {
	private queryService: QueryService = new QueryService("/accounts");
	isLoading: boolean = false;
	isLoadingSummaryReport: boolean = false;
	isLoadingAllUsageData: boolean = false;
	isError: boolean = false;
	rowsPerPage: number = 20;
	usageSummary: IUsageSummary = { monthlyUsage: 0, totalUsage: 0 };
	usageData: IUsageData[] = [];
	allUsageData: IUsageData[] = [];
	tabularUsageData: PaginatedItems<ITableUsageRecord> = { total: 0, items: [] };

	constructor() {
		makeAutoObservable(this);
	}

	clearStoreData = () => {
		runInAction(() => {
			this.isLoading = false;
			this.isLoadingSummaryReport = false;
			this.isLoadingAllUsageData = false;
			this.isError = false;
			this.usageSummary = { monthlyUsage: 0, totalUsage: 0 };
			this.usageData = [];
			this.allUsageData = [];
			this.tabularUsageData = { total: 0, items: [] };
		});
	};

	loadAllUsageData = async (
		startDate: string,
		endDate: string,
		selectedApiRoute: string[] = [],
		usageType: string[],
		selectedTailoredModels: number[] = [],
		getHistoricalData: boolean = false,
		calcUnits: boolean = true,
	): Promise<IUsageData[]> => {
		try {
			this.isError = false;
			this.isLoadingAllUsageData = true;

			const requestData: any = {
				apiRoutes: selectedApiRoute,
				usageType: usageType.map((ut) => ut.toLowerCase()),
				tailoredModels: selectedTailoredModels,
				startDate,
				endDate,
				getHistoricalData,
				calc_units: calcUnits,
			};

			const data: IUsageData[] = await this.queryService.post("/", requestData);

			runInAction(() => {
				this.allUsageData = data;
				this.isLoadingAllUsageData = false;
				this.isError = false;
			});

			return this.allUsageData;
		} catch (err) {
			runInAction(() => {
				this.isError = true;
				this.isLoadingAllUsageData = false;
			});

			return Promise.reject(err);
		}
	};

	// Fetch usage data without mutating store state
	fetchVideoSecondsData = async (
		startDate: string,
		endDate: string,
		selectedApiRoute: string[] = [],
		usageType: string[] = [],
	): Promise<IUsageData[]> => {
		try {
			this.isError = false;
			this.isLoadingAllUsageData = true;

			const requestData: any = {
				apiRoutes: selectedApiRoute,
				usageType: usageType.map((ut) => ut.toLowerCase()),
				startDate,
				endDate,
				calc_video_units: true,
			};

			runInAction(() => {
				this.isLoadingAllUsageData = false;
				this.isError = false;
			});

			return await this.queryService.post("/", requestData);
		} catch (err) {
			runInAction(() => {
				this.isError = true;
				this.isLoadingAllUsageData = false;
			});
			return Promise.reject(err);
		}
	};
}
